import axios from 'src/utils/axios';
import { mediaIdOf, mediaContentPath } from 'src/utils/media';

// ----------------------------------------------------------------------
// Descarga del contenido de un documento desde el endpoint de streaming del BACKEND.
//
// Se pide como `blob` (binario puro): NUNCA como base64 para armar un data URI. El
// binario ya no viaja dentro del JSON, y bajarlo en base64 costaria +33% de red y
// bloquearia el hilo principal decodificandolo.
//
// El JWT viaja en la cabecera Authorization que pone el interceptor global de axios,
// asi que estas llamadas quedan autenticadas igual que el resto del API.
// ----------------------------------------------------------------------

/**
 * Descarga el contenido de un documento.
 *
 * @param {string|object} media mediaId o el objeto MediaBlob/MediaResponseDto.
 * @param {object} [opts]
 * @param {string} [opts.patientId] Ancla la peticion al paciente dueno (comprueba pertenencia).
 * @param {boolean} [opts.inline] Pide el documento para visor en vez de descarga.
 * @param {AbortSignal} [opts.signal] Para cancelar si el componente se desmonta.
 * @returns {Promise<Blob>} El binario.
 */
export const getMediaContentBlob = async (media, { patientId, inline = false, signal } = {}) => {
  const path = mediaContentPath(media, { patientId, inline });
  if (!path) throw new Error('mediaId no valido');

  const response = await axios.get(path, { responseType: 'blob', signal });
  return response.data;
};

/**
 * Metadatos del documento (sin binario): fileName, contentType, sizeBytes, uploadDate, url.
 * Pasa por la MISMA autorizacion que la descarga, asi que puede devolver 404.
 */
export const getMediaMetadata = async (media, { signal } = {}) => {
  const id = mediaIdOf(media);
  if (!id) throw new Error('mediaId no valido');

  const response = await axios.get(`/api/Media/${id}`, { signal });
  return response.data;
};

/**
 * Descarga un documento provocando el dialogo de guardado del navegador.
 *
 * @param {string|object} media mediaId o el objeto MediaBlob.
 * @param {string} fileName Nombre sugerido para el archivo guardado.
 * @param {object} [opts] Igual que en {@link getMediaContentBlob}.
 */
export const downloadMediaContent = async (media, fileName, opts = {}) => {
  const blob = await getMediaContentBlob(media, opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'documento';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Contenido del documento en base64 SIN el prefijo `data:`.
 *
 * Solo para los formularios que tienen que MANIPULAR los bytes en el navegador
 * (rotar la cédula escaneada en un canvas y volver a subirla). Para MOSTRAR una
 * imagen usa MediaImage / useMediaObjectUrl: pintar un data URI obliga a decodificar
 * el base64 en el hilo principal y no aporta nada.
 */
export const getMediaContentBase64 = async (media, opts = {}) => {
  const blob = await getMediaContentBlob(media, opts);

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el documento'));
    reader.onload = () => {
      const resultado = String(reader.result ?? '');
      resolve(resultado.slice(resultado.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
};
